import { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface NudgePendingButtonProps {
  gameId: string;
  variant?: "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg";
  className?: string;
  fullWidth?: boolean;
}

/**
 * Reusable button for coaches/managers to send a "nudge" push notification
 * to parents whose RSVP is still pending. Wraps the `send-coach-nudge` edge
 * function and surfaces cooldown / empty-recipient feedback as toasts.
 */
export function NudgePendingButton({
  gameId,
  variant = "outline",
  size = "sm",
  className,
  fullWidth = false,
}: NudgePendingButtonProps) {
  const [isNudging, setIsNudging] = useState(false);

  const handleClick = async () => {
    setIsNudging(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-coach-nudge", {
        body: { gameId },
      });
      if (error) throw error;
      if (data?.cooldownHoursRemaining) {
        toast.error(
          `Already nudged in the last 24h. Try again in ~${data.cooldownHoursRemaining}h.`
        );
        return;
      }
      if (data?.error) {
        if (data?.serverConfigError) {
          toast.error(data.error);
        } else {
          toast.error(data.error);
        }
        return;
      }
      const sentCount = data?.sent ?? 0;
      if (sentCount === 0) {
        toast.info(data?.reason || "Nothing to send right now.");
        return;
      }
      toast.success(
        `Nudged ${data.recipients} parent(s) on ${sentCount} device(s).`
      );
    } catch (err: any) {
      toast.error(err?.message || "Could not send nudge.");
    } finally {
      setIsNudging(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={`gap-1 ${fullWidth ? "w-full" : ""} ${className ?? ""}`}
      onClick={handleClick}
      disabled={isNudging}
    >
      <Bell className="h-4 w-4" />
      <span>{isNudging ? "Nudging…" : "Nudge pending RSVPs"}</span>
    </Button>
  );
}
